import { useState, type FormEvent } from "react";
import { brandName, navLinks, categories } from "@/mocks/homeData";

const FORM_URL = "https://readdy.ai/api/form/daftp0ptto0ceqqnhhdg";

const principles = [
  { icon: "ri-shield-check-line", text: "有信源才吃瓜" },
  { icon: "ri-time-line", text: "动态每10分钟刷新" },
  { icon: "ri-quill-pen-line", text: "失实内容即时订正" },
  { icon: "ri-community-line", text: "不渲染对立情绪" },
];

export default function FooterSection() {
  return (
    <footer className="bg-secondary-100 text-foreground-900">
      <section id="join" className="mx-auto max-w-7xl px-4 py-14 md:px-6" style={{ scrollMarginTop: 84 }}>
        <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
          {/* 品牌与原则 */}
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                <i className="ri-radar-fill text-lg" />
              </span>
              <span className="font-heading text-2xl font-black text-foreground-950">
                {brandName}
              </span>
            </div>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-foreground-700">
              一个专注追踪网红、主播、达人与大V最新动态的内容观察站。我们记录日常更新、内容转型与商业动作，
              也拆解涨粉背后的方法。有信源、有态度、有边界，是我站一贯的内容原则。
            </p>
            <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {principles.map((p) => (
                <li key={p.text} className="inline-flex items-center gap-2 text-sm text-foreground-800">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-background-50 text-primary-600 ring-1 ring-primary-200">
                    <i className={p.icon} />
                  </span>
                  {p.text}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground-700">
              <a href="mailto:hello@guaradar.site" className="inline-flex items-center gap-1.5 hover:text-primary-700">
                <i className="ri-mail-line" />
                hello@guaradar.site
              </a>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-edit-line" />
                投稿与纠错请见右侧表单
              </span>
            </div>
          </div>

          {/* 订阅表单 */}
          <SubscribeForm />
        </div>
      </section>

      <div className="border-t border-secondary-300/60">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <div className="grid gap-8 sm:grid-cols-3">
            <div>
              <h3 className="font-heading text-base font-bold text-foreground-950">快速入口</h3>
              <ul className="mt-3 space-y-2">
                {navLinks.map((l) => (
                  <li key={l.id}>
                    <a href={`#${l.id}`} className="text-sm text-foreground-700 hover:text-primary-700">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-heading text-base font-bold text-foreground-950">追踪栏目</h3>
              <ul className="mt-3 space-y-2">
                {categories.map((c) => (
                  <li key={c.id}>
                    <a href="#latest" className="text-sm text-foreground-700 hover:text-primary-700">
                      {c.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-heading text-base font-bold text-foreground-950">关于本站</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                吃瓜雷达把"追踪公众人物公开动态"当作一份工作，而非情绪出口。愿你的每次刷新，都有值得读的一手信息。
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-secondary-300/60 bg-secondary-200/70">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 sm:flex-row md:px-6">
          <p className="text-xs text-foreground-600">
            © {new Date().getFullYear()} {brandName} · 本站内容基于公开信息整理观察，数据仅作趋势参考，不代表官方口径。
          </p>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-50 px-4 py-2 text-xs font-semibold text-foreground-800 ring-1 ring-secondary-300 transition-colors hover:text-primary-700"
          >
            <i className="ri-arrow-up-line" />
            返回顶部
          </button>
        </div>
      </div>
    </footer>
  );
}

function SubscribeForm() {
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [message, setMessage] = useState("");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honey = ((fd.get("company_alt") as string) || "").trim();

    if (honey) {
      // 蜜罐命中：给出与正常一致的反馈，不向表单地址提交
      setStatus("ok");
      setMessage("订阅成功，欢迎加入瓜友日报！");
      form.reset();
      return;
    }

    const email = ((fd.get("email") as string) || "").trim();
    if (!email) {
      setStatus("error");
      setMessage("请填写有效的邮箱地址");
      return;
    }

    fd.delete("company_alt");
    setStatus("sending");
    setMessage("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let code = "";
      let serverMsg = "";
      try {
        const parsed = JSON.parse(text);
        code = parsed?.code ?? "";
        serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      } catch {
        serverMsg = text;
      }
      const lower = (serverMsg || "").toLowerCase();
      if (res.ok && code === "OK" && !lower.includes("spam")) {
        setStatus("ok");
        setMessage("订阅成功，瓜友日报将定期送到你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交未成功，请稍后重试");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后重试");
    }
  };

  return (
    <div className="rounded-3xl border border-secondary-300/70 bg-background-50 p-6 shadow-soft md:p-8">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-500 text-background-50">
          <i className="ri-mail-send-line text-lg" />
        </span>
        <h3 className="font-heading text-xl font-bold text-foreground-950">订阅瓜友日报 &amp; 投稿纠错</h3>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-foreground-700">
        留下邮箱即可免费订阅每日动态精选；若你掌握一手线索或发现内容偏差，也可在下方留言，我们会第一时间跟进或订正。
      </p>

      <form
        onSubmit={onSubmit}
        data-readdy-form
        className="mt-5 space-y-4"
        noValidate
      >
        <div>
          <label htmlFor="sub-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            你的邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="sub-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="h-11 w-full rounded-xl border border-background-300 bg-background-50 px-4 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor="sub-cat" className="mb-1.5 block text-sm font-medium text-foreground-800">
            你常关注的栏目
          </label>
          <select
            id="sub-cat"
            name="category"
            className="h-11 w-full appearance-none rounded-xl border border-background-300 bg-background-50 px-4 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="">全部动态</option>
            {categories.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="sub-msg" className="mb-1.5 block text-sm font-medium text-foreground-800">
            想追踪的人物 / 你的线索或纠错（选填）
          </label>
          <textarea
            id="sub-msg"
            name="message"
            rows={3}
            maxLength={500}
            placeholder="例如：希望多追踪某位生活区博主；某条报道有出入，附上链接……"
            className="w-full resize-none rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        {/* 蜜罐字段：仅防机器人，正常用户无需填写 */}
        <input
          type="text"
          name="company_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="feed-honeypot"
        />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            type="submit"
            disabled={status === "sending"}
            className="inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className="ri-send-plane-fill" />
            {status === "sending" ? "提交中…" : "订阅 / 提交"}
          </button>
          <p className="text-xs text-foreground-500">仅用于动态推送，绝不打扰，可随时退订。</p>
        </div>

        {status === "ok" && (
          <p className="flex items-center gap-1.5 rounded-xl bg-accent-100 px-4 py-2.5 text-sm text-accent-900">
            <i className="ri-checkbox-circle-fill" />
            {message}
          </p>
        )}
        {status === "error" && (
          <p className="flex items-center gap-1.5 rounded-xl bg-primary-100 px-4 py-2.5 text-sm text-primary-800">
            <i className="ri-error-warning-fill" />
            {message}
          </p>
        )}
      </form>
    </div>
  );
}